// services/qboApi.ts - API client for QBO Sync server

let apiUrl = import.meta.env.VITE_API_URL || '';
const API_KEY = import.meta.env.VITE_API_KEY || '';
let authToken: string | null = null;

// The v3 .NET agent serves the OAuth routes at the root (`/qbo/auth`,
// `/qbo/status`, ...) while every sync route lives under `/api/sync/...`.
// `raw` skips the `/api` prefix for those root-level routes.
async function request(method: string, path: string, body: unknown = null, raw = false) {
  const url = raw ? `${apiUrl}${path}` : `${apiUrl}/api${path}`;
  const headers: Record<string, string> = {
    'x-api-key': API_KEY,
    'Content-Type': 'application/json',
  };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const options: RequestInit = { method, headers };
  if (body) options.body = JSON.stringify(body);

  const response = await fetch(url, options);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `Request failed: ${response.status}`);
  }
  return data;
}

const qboApi = {
  // Auth
  setAuthToken: (token: string) => { authToken = token; },
  setApiUrl: (url: string) => { apiUrl = url; },

  // Health
  health: () => request('GET', '/health'),

  // QBO OAuth — root-level routes on the agent (no /api prefix)
  getAuthUrl: () => request('GET', '/qbo/auth', null, true),
  getQBOStatus: () => request('GET', '/qbo/status', null, true),
  refreshToken: () => request('POST', '/qbo/refresh', null, true),
  testConnection: () => request('GET', '/qbo/test', null, true),

  // Customer Sync
  previewCustomers: () => request('GET', '/sync/customers/preview'),
  syncCustomers: () => request('POST', '/sync/customers'),
  resendCustomer: (code: string) => request('POST', `/sync/customers/resend/${code}`),

  // Invoice Sync
  previewInvoices: (limit = 20) => request('GET', `/sync/invoices/preview?limit=${limit}`),
  syncInvoices: (limit = 10) => request('POST', `/sync/invoices?limit=${limit}`),
  resendInvoice: (inv: string) => request('POST', `/sync/invoices/resend/${inv}`),

  // Payment Sync
  previewPayments: (limit = 20) => request('GET', `/sync/payments/preview?limit=${limit}`),
  syncPayments: (limit = 10) => request('POST', `/sync/payments?limit=${limit}`),
  resendPayment: (receipt: string) => request('POST', `/sync/payments/resend/${receipt}`),

  // Purchase Sync
  previewPurchases: (limit = 20) => request('GET', `/sync/purchases/preview?limit=${limit}`),
  syncPurchases: (limit = 10) => request('POST', `/sync/purchases?limit=${limit}`),
  resendPurchase: (po: string) => request('POST', `/sync/purchases/resend/${po}`),

  // Vendor Sync
  previewVendors: (limit = 20) => request('GET', `/sync/vendors/preview?limit=${limit}`),
  syncVendors: (limit = 10) => request('POST', `/sync/vendors?limit=${limit}`),
  resendVendor: (code: string) => request('POST', `/sync/vendors/resend/${code}`),

  // 3.17.0 — review a date range, push a selection (entity = customers|invoices|payments|purchases|vendors)
  review: (entity: string, from: string, to: string) =>
    request('GET', `/sync/${entity}/review?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  // 3.19: allowCap = the operator acknowledged over-applied receipts; they are posted capped at the QuickBooks balances
  push: (entity: string, keys: string[], opts?: { allowCap?: boolean }) => request('POST', `/sync/${entity}/push`, { keys, allowCap: opts?.allowCap === true }),

  // 3.19.3 — reconciliation audit (read-only, background job) + 3.19.2 pre-cutoff on-account journal + 3.19.4 baseline
  auditRun: () => request('POST', '/sync/audit/run', {}),
  auditLatest: () => request('GET', '/sync/audit/latest'),
  auditSectionPage: (id: string, section: string, page: number) => request('GET', `/sync/audit/${encodeURIComponent(id)}/${encodeURIComponent(section)}/page/${page}`),
  onAccountPlan: () => request('GET', '/sync/onaccount/precutoff'),
  onAccountPost: (date?: string) => request('POST', '/sync/onaccount/precutoff/post', date ? { date } : {}),
  paymentsBaseline: () => request('POST', '/sync/payments/baseline', {}),

  // 3.17.0 — open-receivables conversion at the cutoff
  conversionConfig: () => request('GET', '/sync/conversion/config'),
  conversionReview: (cutoff?: string, since?: string) => {
    const q = new URLSearchParams();
    if (cutoff) q.set('cutoff', cutoff);
    if (since) q.set('since', since);
    const qs = q.toString();
    return request('GET', `/sync/conversion/review${qs ? `?${qs}` : ''}`);
  },
  conversionRun: (keys: string[], cutoff?: string, offsetDate?: string) =>
    request('POST', '/sync/conversion/run', { keys, cutoff: cutoff || null, offsetDate: offsetDate || null }),
  resetAccounts: () => request('POST', '/sync/accounts/reset'),

  // 3.18.7 — before/after snapshots of the QuickBooks company around a conversion batch
  conversionSnapshot: (label?: string) => request('POST', `/sync/conversion/snapshot${label ? `?label=${encodeURIComponent(label)}` : ''}`),
  conversionSnapshots: () => request('GET', '/sync/conversion/snapshots'),
  conversionLumps: () => request('GET', '/sync/conversion/lumps'),
  // 3.18.14 — Desktop-migration cleanup (plan is read-only; run returns 202 + job unless ?wait=true)
  conversionCleanupPlan: () => request('GET', '/sync/conversion/cleanup/plan'),
  conversionCleanupRun: (body: { qboIds?: string[]; all?: boolean; date?: string; excludeIds?: string[] }) => request('POST', '/sync/conversion/cleanup/run', body),
  // 3.23.2 — undo a customer's last cleanup run (plan is read-only)
  conversionCleanupUndoPlan: (qboIds: string[]) => request('GET', `/sync/conversion/cleanup/undo/plan?qboIds=${encodeURIComponent(qboIds.join(','))}`),
  conversionCleanupUndo: (qboIds: string[]) => request('POST', '/sync/conversion/cleanup/undo', { qboIds }),
  // 3.18.15 — undo the conversion of a cutoff exception day (plan is read-only; run returns 202 + job unless ?wait=true)
  conversionUnconvertPlan: (day: string) => request('GET', `/sync/conversion/unconvert/plan?day=${encodeURIComponent(day)}`),
  conversionUnconvertRun: (day: string) => request('POST', `/sync/conversion/unconvert?day=${encodeURIComponent(day)}`),
  conversionCustomer: (code: number) => request('GET', `/sync/conversion/customer?code=${code}`),
  // 3.23.0 — re-sync customers to Advantage (plan is read-only; run returns 202 + job unless ?wait=true)
  // 3.26.0: Advantage write-back (SentToQB / Deposit stamps) backfill
  writeBackPlan: (force = false) => request('GET', `/sync/writeback/plan${force ? '?force=true' : ''}`),
  writeBackRun: (force = false) => request('POST', `/sync/writeback/run${force ? '?force=true' : ''}`),
  // 3.25.5: pre-cutoff cash applied after the cutoff
  preCashPlan: (codes: number[]) => request('GET', `/sync/conversion/precash/plan?codes=${codes.join(',')}`),
  preCashRun: (body: { codes: number[]; date?: string | null }) => request('POST', '/sync/conversion/precash/run', body),
  conversionResyncPlan: (code: number, postDated = true) => request('GET', `/sync/conversion/resync/plan?code=${code}${postDated ? '' : '&postDated=false'}`),
  conversionResyncRun: (body: { codes: number[]; pushDocuments?: boolean; pushReceipts?: boolean; offsetDate?: string | null; includePostDated?: boolean }) => request('POST', '/sync/conversion/resync', body),
  conversionCompare: (a: string, b: string) => request('GET', `/sync/conversion/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`),

  // 3.17.0 — background jobs (push / conversion return 202 + { jobId }; poll until status != running)
  getJob: (id: string) => request('GET', `/sync/jobs/${encodeURIComponent(id)}`),
  listJobs: () => request('GET', '/sync/jobs'),
  cancelJob: (id: string) => request('POST', `/sync/jobs/${encodeURIComponent(id)}/cancel`),

  // Sync Log
  getSummary: () => request('GET', '/sync/summary'),
  getActivity: (limit = 50) => request('GET', `/sync/activity?limit=${limit}`),
  getErrors: () => request('GET', '/sync/errors'),
  markResend: (tableName: string, recordKey: string) =>
    request('POST', '/sync/mark-resend', { tableName, recordKey }),
};

export default qboApi;
